import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Search, Database, FileDown, HardDrive, Cloud } from "lucide-react";
import { toast } from "sonner";
import { api, fmtCHF, fmtDate, downloadFile } from "@/lib/api";
import { CHANNEL_ICONS, CHANNEL_LABELS, TRUST, DOC_STATUS, FACT_TYPE_LABELS,
         VERIFICATION_LABELS, ACCOUNTING_STATUS_LABELS, ARCHIVE_TABS } from "@/lib/organs";
import { SectionTitle, DemoBanner } from "@/components/Shared";
import DocDetail from "@/components/DocDetail";

export default function Archive() {
  const [tab, setTab] = useState("documenti");
  const [docs, setDocs] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [company, setCompany] = useState(null);
  const [q, setQ] = useState("");
  const [docType, setDocType] = useState("");
  const [statusF, setStatusF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [archiveCat, setArchiveCat] = useState("");
  const [detail, setDetail] = useState(null);

  const loadDocs = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (docType) p.set("doc_type", docType);
    if (statusF) p.set("status", statusF);
    if (archiveCat) p.set("archive_category", archiveCat);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    api.get(`/documents?${p.toString()}`).then((r) => setDocs(r.data)).catch(() => {});
  };

  useEffect(() => { loadDocs(); }, [docType, statusF, dateFrom, dateTo, archiveCat]);
  useEffect(() => {
    api.get("/evidence").then((r) => setEvidence(r.data)).catch(() => {});
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
  }, []);

  const docTypes = useMemo(() => [...new Set(docs.map((d) => d.doc_type).filter(Boolean))], [docs]);

  const openDetail = async (docId) => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      setDetail(data);
    } catch { toast.error("Impossibile aprire il documento"); }
  };

  const exportBundle = async () => {
    try {
      await downloadFile(`/companies/${company.company_id}/export`, `dad-export-${company.company_id}.json`);
      toast.success("Export workspace scaricato");
    } catch { toast.error("Export non riuscito"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4" data-testid="archive-page">
      <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-300 mb-4"><ChevronLeft size={14} /> Organismo</Link>
      <div className="flex items-start justify-between gap-3 mb-5 fade-up">
        <div>
          <p className="text-xs tracking-[0.25em] text-gray-500">MEMORIA · METAFORA ORGANISMO</p>
          <h1 className="font-display text-3xl font-bold mt-1">Archivio & Documenti</h1>
          <p className="text-sm text-gray-500 mt-1">Un solo originale per documento, molte relazioni. Zero lock-in: tutto è esportabile.</p>
        </div>
        {company?.demo && <DemoBanner />}
      </div>

      <div className="flex gap-2 mb-5" data-testid="archive-tabs">
        {[["documenti", "Documenti"], ["evidenze", "Registro probatorio"], ["esporta", "Esporta & Storage"]].map(([k, l]) => (
          <button key={k} data-testid={`archive-tab-${k}`} onClick={() => setTab(k)}
            className={`chip !cursor-pointer !py-2 transition-colors ${tab === k ? "!text-cyan-200 !border-cyan-400/50 !bg-cyan-400/10" : ""}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "documenti" && (
        <>
          <div className="relative mb-3">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-600" />
            <input data-testid="archive-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadDocs()}
              className="input-dark !pl-11" placeholder="Cerca per fornitore, tipo, categoria, file… (Invio)" />
          </div>
          <div className="flex gap-2 flex-wrap mb-5" data-testid="archive-filters">
            <select data-testid="archive-filter-type" value={docType} onChange={(e) => setDocType(e.target.value)} className="input-dark !w-auto !py-2 text-xs">
              <option value="">Tipo: tutti</option>
              {docTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select data-testid="archive-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)} className="input-dark !w-auto !py-2 text-xs">
              <option value="">Stato: tutti</option>
              {Object.entries(DOC_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input data-testid="archive-filter-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-dark !w-auto !py-2 text-xs" title="Da data" />
            <input data-testid="archive-filter-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-dark !w-auto !py-2 text-xs" title="A data" />
          </div>

          <div className="flex gap-1.5 flex-wrap mb-4" data-testid="archive-categories">
            {ARCHIVE_TABS.map(([k, l]) => (
              <button key={k} data-testid={`archive-cat-${k || "tutti"}`} onClick={() => setArchiveCat(k)}
                className={`chip !cursor-pointer !text-[11px] transition-colors ${archiveCat === k ? "!text-cyan-200 !border-cyan-400/50 !bg-cyan-400/10" : ""}`}>
                {l}
              </button>
            ))}
          </div>

          <div className="space-y-2.5" data-testid="archive-list">
            {docs.map((d) => {
              const Icon = CHANNEL_ICONS[d.channel] || CHANNEL_ICONS.file;
              const trust = TRUST[d.trust];
              const st = DOC_STATUS[d.status];
              return (
                <button key={d.doc_id} data-testid={`archive-doc-${d.doc_id}`} onClick={() => openDetail(d.doc_id)}
                  className="w-full text-left glass rounded-2xl px-4 py-3.5 flex items-center gap-3 hover:border-cyan-400/30 transition-colors">
                  <Icon size={17} className="text-cyan-300 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-200 truncate">{d.supplier || d.filename}</div>
                    <div className="text-[11px] text-gray-500 truncate">
                      {d.doc_type} · {CHANNEL_LABELS[d.channel]} · {fmtDate(d.date)}{d.category ? ` · ${d.category}` : ""}
                    </div>
                  </div>
                  {st && <span className="text-[10px] shrink-0 hidden sm:block" style={{ color: st.color }}>{st.label}</span>}
                  <span className="chip !text-[10px] shrink-0 hidden sm:inline-flex" style={{ color: trust?.color }}>{trust?.label}</span>
                  <span className="font-mono text-sm shrink-0">{fmtCHF(d.amount)}</span>
                </button>
              );
            })}
            {docs.length === 0 && (
              <div className="glass rounded-3xl p-10 text-center text-gray-500 text-sm" data-testid="archive-empty">
                <Database size={24} className="mx-auto mb-2 text-gray-600" />
                Archivio vuoto o nessun risultato. DAD non inventa documenti: acquisisci il primo con DAD Scan.
              </div>
            )}
          </div>
        </>
      )}

      {tab === "evidenze" && (
        <div className="space-y-2.5" data-testid="evidence-list">
          {evidence.map((ev) => {
            const vs = VERIFICATION_LABELS[ev.verification_status] || VERIFICATION_LABELS.non_verificato;
            const as = ACCOUNTING_STATUS_LABELS[ev.accounting_status] || ACCOUNTING_STATUS_LABELS.non_richiesto;
            return (
              <div key={ev.evidence_id} data-testid={`evidence-${ev.evidence_id}`} className="glass rounded-2xl px-4 py-3.5">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-gray-200">{ev.counterparty || ev.source_reference || "—"}</div>
                    <div className="text-[11px] text-gray-500">
                      {FACT_TYPE_LABELS[ev.business_fact_type] || ev.business_fact_type} · {fmtDate(ev.date)}
                    </div>
                  </div>
                  <span className="chip !text-[10px]" style={{ color: vs.color }}>{vs.label}</span>
                  <span className="chip !text-[10px]" style={{ color: as.color }}>{as.label}</span>
                  <span className="font-mono text-sm">{fmtCHF(ev.amount)}</span>
                </div>
                {ev.next_action && <div className="text-[11px] text-amber-300/80 mt-2">Prossima azione: {ev.next_action}</div>}
                <button data-testid={`evidence-doc-${ev.evidence_id}`} onClick={() => openDetail(ev.source_document_id)}
                  className="text-[11px] text-cyan-300 hover:text-cyan-200 mt-2">
                  → Documento originale e pipeline
                </button>
              </div>
            );
          })}
          {evidence.length === 0 && (
            <div className="glass rounded-3xl p-10 text-center text-gray-500 text-sm" data-testid="evidence-empty">
              Registro probatorio vuoto. Ogni fatto aziendale verificato apparirà qui, collegato al documento originale.
            </div>
          )}
        </div>
      )}

      {tab === "esporta" && (
        <div className="space-y-4" data-testid="archive-export">
          <div className="glass rounded-3xl p-5">
            <SectionTitle>Storage documentale</SectionTitle>
            <div className="space-y-2">
              {(company?.storage_connections || []).map((c) => (
                <div key={c.connection_id} className="flex items-center gap-3 rounded-2xl px-3 py-2.5 border border-emerald-400/15 bg-emerald-400/5">
                  <HardDrive size={16} className="text-emerald-400" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-200">Storage interno di sviluppo</div>
                    <div className="text-[11px] text-gray-500">Originali immutabili con checksum sha256 · stesso adapter dei provider cloud</div>
                  </div>
                  <span className="chip !text-[10px] !text-emerald-300">Attivo</span>
                </div>
              ))}
              {[["Google Drive", "Repository documentale dell'azienda"], ["Microsoft OneDrive / SharePoint", "Repository documentale dell'azienda"]].map(([name, sub]) => (
                <div key={name} className="flex items-center gap-3 rounded-2xl px-3 py-2.5 border border-white/5 opacity-70">
                  <Cloud size={16} className="text-gray-500" />
                  <div className="flex-1">
                    <div className="text-sm text-gray-300">{name}</div>
                    <div className="text-[11px] text-gray-500">{sub} · gli originali restano di proprietà dell'azienda</div>
                  </div>
                  <span className="chip !text-[10px]">Pianificato — non configurato</span>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-gray-600 mt-3 leading-relaxed">
              DAD conserva l'intelligence layer: metadati, indici, relazioni, dati estratti, stato di lavorazione,
              audit trail ed evidence. L'originale resta referenziato (provider, object key, checksum, versione)
              e sempre esportabile.
            </p>
          </div>
          <button data-testid="archive-export-bundle" onClick={exportBundle} className="btn-primary w-full !py-3.5">
            <FileDown size={16} /> Esporta workspace completo (JSON)
          </button>
        </div>
      )}

      <DocDetail detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
